import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RegulatoryPhaseGate } from "@shared/hooks/useFeatureFlags";
import type { ClinicalEvent } from "../types/clinicalEvent";
import type { DetectedSpike, SeizureEvent } from "../types/detectionTypes";
import {
  createClinicalEvent,
  importFromDetectorRun,
  listTriageClinicalEvents,
  updateClinicalEvent,
} from "../services/eegEventApi";
import { completeAiTriageCloseDraft } from "../services/aiTriageCloseDraft";
import {
  buildAITriageQueue,
  buildDispositionUpdate,
  filterAITriageQueue,
  queueSummary,
} from "../utils/aiTriageQueue";
import {
  candidateToExplanationArtifact,
  evaluateReportExplainability,
} from "../utils/aiTriageExplainability";
import {
  AI_TRIAGE_CATEGORIES,
  AI_TRIAGE_CATEGORY_LABELS,
  AI_TRIAGE_FALSE_POSITIVE_LABELS,
  type AITriageCandidate,
  type AITriageCandidateCategory,
  type AITriageDispositionStatus,
  type AITriageFalsePositiveClass,
} from "../utils/aiTriageTypes";
import {
  buildDetectorPerfDashboard,
  getAITriageLabelingBundle,
} from "../utils/aiTriageMarketParity";
import { runLocalDetectors } from "../utils/localDetectorRun";
import {
  EegDockPanelActions,
  EegDockPanelCard,
  EegDockPanelChip,
  EegDockPanelField,
  EegDockPanelLabeling,
  EegDockPanelList,
  EegDockPanelStatus,
  EegDockPanelToolbar,
} from "./EegDockPanelChrome";
import {
  AI_TRIAGE_IFU_UI_BADGE,
  AI_TRIAGE_IFU_UI_SUMMARY,
} from "../utils/assistiveIfuFreeze";
import "./AITriageQueuePanel.css";

/** S3 alert policy (assistive IFU): Complete → attending notify; STAT seizure is opt-in clinician escalation only. */
export const AI_TRIAGE_ALERT_POLICY =
  "Alert policy: Complete triage notifies the assigned attending. Interim draft never pages. Optional STAT seizure escalate is clinician-initiated only — never unattended automatic alarming from detectors.";

export type AITriageQueuePanelProps = {
  recordingId?: string | null;
  studyId?: string | null;
  spikes?: DetectedSpike[];
  seizures?: SeizureEvent[];
  channelNames?: string[];
  channelSeries?: Array<{ name: string; data?: Float32Array | number[] | null }>;
  samplingRate?: number;
  recordingDurationSeconds?: number;
  reviewerId?: string | null;
  attendingUserId?: string | null;
  onSeek?: (timeSeconds: number) => void;
  /** Optional Live Scalp Localize event-field hint when Jump is used. */
  onFieldEventHint?: (hint: {
    startSeconds: number;
    endSeconds: number;
    label: string;
  }) => void;
};

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);
  return `${mins}:${secs.padStart(4, "0")}`;
}

function AITriageQueuePanelInner({
  recordingId,
  studyId,
  spikes = [],
  seizures = [],
  channelNames = [],
  channelSeries = [],
  samplingRate = 256,
  recordingDurationSeconds = 0,
  reviewerId = null,
  attendingUserId = null,
  onSeek,
  onFieldEventHint,
}: AITriageQueuePanelProps) {
  const [clinicalEvents, setClinicalEvents] = useState<ClinicalEvent[]>([]);
  const [localSpikes, setLocalSpikes] = useState<DetectedSpike[]>([]);
  const [localSeizures, setLocalSeizures] = useState<SeizureEvent[]>([]);
  const [detectorBusy, setDetectorBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importRunId, setImportRunId] = useState("");
  const [importModelCardUri, setImportModelCardUri] = useState("");
  const [importModelVersion, setImportModelVersion] = useState("");
  const [importMinConfidence, setImportMinConfidence] = useState(0.5);
  const [statEscalate, setStatEscalate] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [localOverrides, setLocalOverrides] = useState<
    Record<string, AITriageCandidate>
  >({});
  const [category, setCategory] = useState<AITriageCandidateCategory | "all">(
    "all",
  );
  const [dispositionFilter, setDispositionFilter] = useState<
    AITriageDispositionStatus | "all"
  >("pending");
  const [minConfidence, setMinConfidence] = useState(0.3);
  const [search, setSearch] = useState("");
  const [fpClass, setFpClass] =
    useState<AITriageFalsePositiveClass>("artifact");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready">(
    "idle",
  );
  const [closeBusy, setCloseBusy] = useState<"complete" | "interim" | null>(
    null,
  );
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closeMessage, setCloseMessage] = useState<string | null>(null);
  const [draftReportId, setDraftReportId] = useState<string | null>(null);

  const mergedSpikes = localSpikes.length ? localSpikes : spikes;
  const mergedSeizures = localSeizures.length ? localSeizures : seizures;

  const refreshEvents = useCallback(async () => {
    if (!recordingId) {
      setClinicalEvents([]);
      setLoadState("ready");
      return;
    }
    setLoadState("loading");
    try {
      const events = await listTriageClinicalEvents({
        recordingId,
        studyId: studyId || undefined,
        limit: 1000,
      });
      setClinicalEvents(events);
      setError(null);
    } catch {
      setClinicalEvents([]);
      setError("Could not load persisted AI candidates; showing local detectors.");
    } finally {
      setLoadState("ready");
    }
  }, [recordingId, studyId]);

  useEffect(() => {
    void refreshEvents();
  }, [refreshEvents]);

  const baseQueue = useMemo(
    () =>
      buildAITriageQueue({
        clinicalEvents,
        spikes: mergedSpikes,
        seizures: mergedSeizures,
        channelNames,
      }),
    [clinicalEvents, mergedSpikes, mergedSeizures, channelNames],
  );

  const queue = useMemo(
    () =>
      baseQueue.map((candidate) => localOverrides[candidate.candidateId] || candidate),
    [baseQueue, localOverrides],
  );

  const filtered = useMemo(
    () =>
      filterAITriageQueue(queue, {
        category,
        disposition: dispositionFilter,
        minConfidence,
        search,
      }),
    [queue, category, dispositionFilter, minConfidence, search],
  );

  const categoryFilterActive = category !== "all";
  const selectableFiltered = useMemo(
    () =>
      filtered.filter(
        (c) =>
          c.disposition.status === "pending" ||
          c.disposition.status === "deferred" ||
          c.disposition.status === "escalated",
      ),
    [filtered],
  );
  const selectedInView = useMemo(
    () => selectableFiltered.filter((c) => selectedIds.has(c.candidateId)),
    [selectableFiltered, selectedIds],
  );
  const allVisibleSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((c) => selectedIds.has(c.candidateId));

  useEffect(() => {
    setSelectedIds(new Set());
  }, [category, dispositionFilter, minConfidence, search]);

  const summary = useMemo(() => queueSummary(queue), [queue]);
  const blockingCount =
    summary.pending + summary.deferred + summary.escalated;
  const explanationGate = useMemo(
    () => evaluateReportExplainability(queue),
    [queue],
  );
  const canCompleteTriage =
    blockingCount === 0 && explanationGate.ready;
  const detectorDashboard = useMemo(() => {
    const hours =
      recordingDurationSeconds && recordingDurationSeconds > 0
        ? recordingDurationSeconds / 3600
        : 24;
    return buildDetectorPerfDashboard(queue, hours);
  }, [queue, recordingDurationSeconds]);
  const labeling = useMemo(() => getAITriageLabelingBundle(), []);

  const applyLocal = (candidate: AITriageCandidate) => {
    setLocalOverrides((prev) => ({
      ...prev,
      [candidate.candidateId]: candidate,
    }));
  };

  const dispose = async (
    candidate: AITriageCandidate,
    status: AITriageDispositionStatus,
  ) => {
    setBusyId(candidate.candidateId);
    setError(null);
    const update = buildDispositionUpdate({
      candidate,
      status,
      falsePositiveClass: status === "false_positive" ? fpClass : null,
      reviewerId: reviewerId || "session-reviewer",
      notes:
        status === "false_positive"
          ? AI_TRIAGE_FALSE_POSITIVE_LABELS[fpClass]
          : null,
    });

    let next: AITriageCandidate = {
      ...candidate,
      disposition: update.disposition,
      auditTrail: [...candidate.auditTrail, update.auditEntry],
      payload: update.payload,
      model: update.model,
    };

    try {
      if (candidate.eventId && candidate.rowVersion != null) {
        const saved = await updateClinicalEvent({
          eventId: candidate.eventId,
          rowVersion: candidate.rowVersion,
          eventStatus: update.eventStatus,
          payloadJson: update.payload,
          notes: update.disposition.notes,
        });
        next = {
          ...next,
          eventId: saved.eventId,
          rowVersion: saved.rowVersion ?? (candidate.rowVersion || 0) + 1,
          payload: saved.payload,
          disposition: update.disposition,
        };
      } else if (recordingId && studyId && !candidate.eventId) {
        const created = await createClinicalEvent({
          studyId,
          recordingId,
          eventTypeCode: candidate.eventTypeCode,
          eventLabel: candidate.label,
          startTimeSeconds: candidate.startTimeSeconds,
          endTimeSeconds: candidate.endTimeSeconds ?? undefined,
          channels: candidate.channels,
          confidenceScore: candidate.confidence ?? undefined,
          notes: update.disposition.notes ?? undefined,
          payloadJson: update.payload,
        });
        next = {
          ...next,
          eventId: created.eventId,
          rowVersion: 1,
          payload: created.payload,
        };
        // Persist disposition status if create defaults to active.
        if (update.eventStatus === "suppressed") {
          try {
            const saved = await updateClinicalEvent({
              eventId: created.eventId,
              rowVersion: 1,
              eventStatus: "suppressed",
              payloadJson: update.payload,
            });
            next = {
              ...next,
              rowVersion: saved.rowVersion ?? 2,
              payload: saved.payload,
            };
          } catch {
            // Local override still applies.
          }
        }
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to persist disposition; kept local only.",
      );
    }

    applyLocal(next);
    setBusyId(null);
    return next;
  };

  const toggleSelect = (candidateId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(candidateId);
      else next.delete(candidateId);
      return next;
    });
  };

  const selectAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const candidate of selectableFiltered) {
        if (checked) next.add(candidate.candidateId);
        else next.delete(candidate.candidateId);
      }
      return next;
    });
  };

  const confirmSelected = async () => {
    const targets = [...selectedInView];
    if (targets.length === 0) return;
    setBulkBusy(true);
    setError(null);
    try {
      for (const candidate of targets) {
        await dispose(candidate, "confirmed");
      }
      setSelectedIds(new Set());
      setCloseMessage(
        `Confirmed ${targets.length} selected candidate(s). Review remaining items on raw EEG.`,
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const runLocalDetectorAction = async () => {
    const channels =
      channelSeries.length > 0
        ? channelSeries
        : channelNames.map((name) => ({ name, data: null }));
    if (!channels.some((ch) => ch.data && ch.data.length > 0)) {
      setError(
        "Load a waveform window before running local detectors. Annotation labels alone cannot seed triage confidence.",
      );
      return;
    }
    setDetectorBusy(true);
    setError(null);
    try {
      const result = runLocalDetectors({
        channels,
        sampleRateHz: samplingRate || 256,
      });
      setLocalSpikes(result.spikes);
      setLocalSeizures(result.seizures);
      setCloseMessage(
        `${result.spikes.length} spike(s), ${result.seizures.length} seizure(s) · ${result.provenance.algorithmVersion} · confirm on raw EEG`,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Local detector run failed.",
      );
    } finally {
      setDetectorBusy(false);
    }
  };

  const runImportAction = async () => {
    if (!importRunId.trim() || !importModelCardUri.trim() || !importModelVersion.trim()) {
      setError(
        "Import requires detector run ID, model card URI, and model version.",
      );
      return;
    }
    setImportBusy(true);
    setError(null);
    try {
      const result = await importFromDetectorRun({
        runId: importRunId.trim(),
        modelCardUri: importModelCardUri.trim(),
        modelVersion: importModelVersion.trim(),
        selection: { minConfidence: importMinConfidence },
      });
      setCloseMessage(
        `Imported ${result.importedCount} candidate(s) from run ${result.sourceRunId} (skipped ${result.skippedCount}).`,
      );
      setImportOpen(false);
      await refreshEvents();
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Detector-run import failed governance checks.",
      );
    } finally {
      setImportBusy(false);
    }
  };

  const runDraft = async (mode: "complete" | "interim") => {
    if (!recordingId && !studyId) {
      setCloseError("Study or recording is required to draft a report.");
      return;
    }
    if (mode === "complete" && !canCompleteTriage) {
      setCloseError(
        blockingCount > 0
          ? "Complete triage requires every pending, deferred, and escalated candidate to be confirmed, rejected, or marked false positive."
          : `Complete triage blocked by explainability gate (${explanationGate.warnings.join(", ") || "AI_EXPLANATION_ACK"}). Confirm candidates with saliency/explanation acknowledgement before drafting.`,
      );
      return;
    }
    setCloseBusy(mode);
    setCloseError(null);
    try {
      const result = await completeAiTriageCloseDraft({
        studyId,
        recordingId,
        attendingUserId: attendingUserId || undefined,
        notifyAttending: mode === "complete",
        force: true,
      });
      if (!result.success) {
        throw new Error(
          result.error ||
            (mode === "complete"
              ? "Failed to complete triage"
              : "Failed to save interim draft"),
        );
      }
      setDraftReportId(result.report?.report_id || null);
      const statNote =
        mode === "complete" &&
        statEscalate &&
        queue.some(
          (c) =>
            c.disposition.status === "confirmed" &&
            (c.category === "seizure" ||
              c.eventTypeCode.toLowerCase().includes("seizure")),
        )
          ? " STAT seizure escalate requested — severity elevated for attending review (clinician-initiated; not unattended alarm)."
          : "";
      setCloseMessage(
        (mode === "complete"
          ? result.attendingNotified
            ? "Unsigned draft ready — attending notified. Review and sign on the report page."
            : "Unsigned draft ready. Assign a signing clinician, then review and sign."
          : "Interim unsigned draft saved. Open the draft to continue review (AI cannot sign).") +
          statNote,
      );
    } catch (err) {
      setCloseError(
        err instanceof Error
          ? err.message
          : mode === "complete"
            ? "Failed to close triage and draft report."
            : "Failed to save interim draft.",
      );
    } finally {
      setCloseBusy(null);
    }
  };

  return (
    <div className="ai-triage-queue ai-triage-queue--compact" data-testid="ai-triage-queue">
      <EegDockPanelLabeling
        className="ai-triage-queue__intended-use"
        ariaLabel="AI triage intended use"
        data-testid="ai-triage-intended-use"
        badge={labeling.nonDiagnosticBadge || AI_TRIAGE_IFU_UI_BADGE}
        summary="Prioritizes human review of raw EEG — not for standalone diagnosis."
        intendedUse={labeling.intendedUse || AI_TRIAGE_IFU_UI_SUMMARY}
        excludedUse={labeling.excludedUse}
        extraDetails={
          <p data-testid="ai-triage-alert-policy">{AI_TRIAGE_ALERT_POLICY}</p>
        }
      />

      <div
        className="eeg-dock-panel-chrome__toolbar ai-triage-queue__toolbar ai-triage-queue__toolbar--filters"
        role="toolbar"
        aria-label="AI triage filters"
      >
        <span className="ai-triage-queue__grid-label">Category</span>
        <select
          className="eeg-dock-panel-chrome__control"
          value={category}
          aria-label="Filter AI triage category"
          onChange={(event) =>
            setCategory(event.target.value as AITriageCandidateCategory | "all")
          }
        >
          <option value="all">All</option>
          {AI_TRIAGE_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {AI_TRIAGE_CATEGORY_LABELS[value]}
            </option>
          ))}
        </select>

        <span className="ai-triage-queue__grid-label">Disposition</span>
        <select
          className="eeg-dock-panel-chrome__control"
          value={dispositionFilter}
          aria-label="Filter AI triage disposition"
          onChange={(event) =>
            setDispositionFilter(
              event.target.value as AITriageDispositionStatus | "all",
            )
          }
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="rejected">Rejected</option>
          <option value="false_positive">False positive</option>
        </select>

        <span className="ai-triage-queue__grid-label">
          {`Min confidence ${(minConfidence * 100).toFixed(0)}%`}
        </span>
        <input
          className="eeg-dock-panel-chrome__control ai-triage-queue__confidence"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={minConfidence}
          aria-label="Minimum AI confidence"
          onChange={(event) => setMinConfidence(Number(event.target.value))}
        />

        <span className="ai-triage-queue__grid-label">FP class</span>
        <select
          className="eeg-dock-panel-chrome__control"
          value={fpClass}
          aria-label="False positive classification"
          onChange={(event) =>
            setFpClass(event.target.value as AITriageFalsePositiveClass)
          }
        >
          {(
            Object.keys(AI_TRIAGE_FALSE_POSITIVE_LABELS) as AITriageFalsePositiveClass[]
          ).map((value) => (
            <option key={value} value={value}>
              {AI_TRIAGE_FALSE_POSITIVE_LABELS[value]}
            </option>
          ))}
        </select>

        <span className="ai-triage-queue__grid-label">Search</span>
        <input
          className="eeg-dock-panel-chrome__control ai-triage-queue__search-control"
          type="search"
          value={search}
          aria-label="Search AI triage candidates"
          placeholder="Label, channel, model…"
          onChange={(event) => setSearch(event.target.value)}
        />

        <span className="ai-triage-queue__grid-label">Detectors</span>
        <div
          className="ai-triage-queue__toolbar-btns"
          role="group"
          aria-label="Detector actions"
        >
          <button
            className="eeg-dock-panel-chrome__btn"
            type="button"
            onClick={() => void refreshEvents()}
          >
            Refresh
          </button>
          <button
            className="eeg-dock-panel-chrome__btn"
            type="button"
            data-testid="ai-triage-run-local-detectors"
            disabled={detectorBusy}
            title="Run assistive local spike/seizure detectors on the loaded window with provenance stamps"
            onClick={() => void runLocalDetectorAction()}
          >
            {detectorBusy ? "Detecting…" : "Run local detectors"}
          </button>
          <button
            className="eeg-dock-panel-chrome__btn"
            type="button"
            data-testid="ai-triage-import-toggle"
            onClick={() => setImportOpen((open) => !open)}
          >
            {importOpen ? "Hide import" : "Import detector run"}
          </button>
        </div>
      </div>

      <EegDockPanelToolbar className="ai-triage-queue__toolbar ai-triage-queue__toolbar--draft">
        <div className="eeg-dock-panel-chrome__field ai-triage-queue__draft-field">
          <span className="eeg-dock-panel-chrome__field-label ai-triage-queue__field-label">
            Draft
          </span>
          <button
            className="eeg-dock-panel-chrome__btn ai-triage-queue__interim"
            type="button"
            data-testid="ai-triage-interim-draft"
            disabled={Boolean(closeBusy) || (!recordingId && !studyId)}
            title="Save an unsigned draft from confirmed events only. Does not notify the attending."
            aria-label="Save interim unsigned draft without notifying attending"
            onClick={() => void runDraft("interim")}
          >
            {closeBusy === "interim" ? "Saving draft…" : "Save interim draft"}
          </button>
        </div>
        <div className="eeg-dock-panel-chrome__field ai-triage-queue__draft-field">
          <span className="eeg-dock-panel-chrome__field-label ai-triage-queue__field-label">
            Escalate
          </span>
          <label
            className="ai-triage-queue__stat-escalate eeg-dock-panel-chrome__control"
          >
            <input
              type="checkbox"
              data-testid="ai-triage-stat-escalate"
              checked={statEscalate}
              onChange={(event) => setStatEscalate(event.target.checked)}
            />
            <span>STAT seizure escalate (opt-in)</span>
          </label>
        </div>
        <div className="eeg-dock-panel-chrome__field ai-triage-queue__draft-field">
          <span className="eeg-dock-panel-chrome__field-label ai-triage-queue__field-label">
            Complete
          </span>
          <button
            className="eeg-dock-panel-chrome__btn ai-triage-queue__complete"
            type="button"
            data-testid="ai-triage-complete"
            disabled={
              Boolean(closeBusy) ||
              (!recordingId && !studyId) ||
              !canCompleteTriage
            }
            title={
              canCompleteTriage
                ? "Draft from confirmed events and notify the assigned attending"
                : blockingCount > 0
                  ? "Confirm, reject, or mark false positive for every pending, deferred, and escalated candidate first"
                  : "Confirm candidates with saliency/explanation acknowledgement before Complete triage"
            }
            aria-label="Complete triage, draft unsigned report, and notify attending"
            onClick={() => void runDraft("complete")}
          >
            {closeBusy === "complete" ? "Drafting…" : "Complete triage"}
          </button>
        </div>
        {categoryFilterActive ? (
          <div className="eeg-dock-panel-chrome__field ai-triage-queue__draft-field ai-triage-queue__bulk-field">
            <span className="eeg-dock-panel-chrome__field-label ai-triage-queue__field-label">
              Bulk (
              {AI_TRIAGE_CATEGORY_LABELS[category]}
              )
            </span>
            <div className="ai-triage-queue__bulk-actions">
              <label className="ai-triage-queue__stat-escalate eeg-dock-panel-chrome__control">
                <input
                  type="checkbox"
                  data-testid="ai-triage-select-all"
                  checked={allVisibleSelected}
                  disabled={selectableFiltered.length === 0 || bulkBusy}
                  onChange={(event) => selectAllVisible(event.target.checked)}
                />
                <span>
                  Select all ({selectableFiltered.length})
                </span>
              </label>
              <button
                className="eeg-dock-panel-chrome__btn"
                type="button"
                data-testid="ai-triage-confirm-selected"
                disabled={selectedInView.length === 0 || bulkBusy}
                title="Confirm every selected pending/deferred/escalated candidate in the current filter"
                onClick={() => void confirmSelected()}
              >
                {bulkBusy
                  ? "Confirming…"
                  : `Confirm selected (${selectedInView.length})`}
              </button>
            </div>
          </div>
        ) : null}
      </EegDockPanelToolbar>

      {importOpen ? (
        <div
          className="ai-triage-queue__import"
          data-testid="ai-triage-import-form"
        >
          <EegDockPanelField label="Detector run ID">
            <input
              className="eeg-dock-panel-chrome__control"
              value={importRunId}
              onChange={(e) => setImportRunId(e.target.value)}
              placeholder="UUID"
              aria-label="Detector run ID"
            />
          </EegDockPanelField>
          <EegDockPanelField label="Model card URI">
            <input
              className="eeg-dock-panel-chrome__control"
              value={importModelCardUri}
              onChange={(e) => setImportModelCardUri(e.target.value)}
              placeholder="qms://model-card/…"
              aria-label="Model card URI"
            />
          </EegDockPanelField>
          <EegDockPanelField label="Model version">
            <input
              className="eeg-dock-panel-chrome__control"
              value={importModelVersion}
              onChange={(e) => setImportModelVersion(e.target.value)}
              placeholder="1.0.0"
              aria-label="Model version"
            />
          </EegDockPanelField>
          <EegDockPanelField label="Min confidence">
            <input
              className="eeg-dock-panel-chrome__control"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={importMinConfidence}
              onChange={(e) =>
                setImportMinConfidence(Number.parseFloat(e.target.value) || 0)
              }
              aria-label="Minimum import confidence"
            />
          </EegDockPanelField>
          <button
            className="eeg-dock-panel-chrome__btn"
            type="button"
            data-testid="ai-triage-import-submit"
            disabled={importBusy}
            onClick={() => void runImportAction()}
          >
            {importBusy ? "Importing…" : "Import with provenance"}
          </button>
        </div>
      ) : null}

      <EegDockPanelStatus
        className="ai-triage-queue__summary"
        aria-label="AI triage queue summary"
        data-testid="ai-triage-perf-dashboard"
      >
        <EegDockPanelChip>{summary.total} candidates</EegDockPanelChip>
        <EegDockPanelChip>{summary.pending} pending</EegDockPanelChip>
        <EegDockPanelChip>{summary.confirmed} confirmed</EegDockPanelChip>
        <EegDockPanelChip>{summary.rejected} rejected</EegDockPanelChip>
        <EegDockPanelChip>{summary.falsePositive} FP</EegDockPanelChip>
        {summary.deferred > 0 && (
          <EegDockPanelChip>{summary.deferred} deferred</EegDockPanelChip>
        )}
        {summary.escalated > 0 && (
          <EegDockPanelChip>{summary.escalated} escalated</EegDockPanelChip>
        )}
        {recordingDurationSeconds >= 36 && (
          <EegDockPanelChip>
            {recordingDurationSeconds >= 3600
              ? `${(recordingDurationSeconds / 3600).toFixed(1)} h`
              : `${Math.round(recordingDurationSeconds / 60)} min`}{" "}
            recording
          </EegDockPanelChip>
        )}
        <EegDockPanelChip title="False positives per 24h (assistive metric)">
          FP/24h {detectorDashboard.falsePositivesPer24h.toFixed(2)}
        </EegDockPanelChip>
        <EegDockPanelChip title="Confirmation rate among reviewed candidates">
          Confirm {detectorDashboard.confirmationRatePercent.toFixed(0)}%
        </EegDockPanelChip>
        <EegDockPanelChip
          title={
            detectorDashboard.reportImpactingEligible
              ? "Confirmed + explainable candidates may contribute to report counts"
              : "Report-impacting AI counts blocked until candidates are confirmed with explanation"
          }
        >
          {detectorDashboard.reportImpactingEligible
            ? "Report AI: eligible"
            : "Report AI: blocked"}
        </EegDockPanelChip>
        {loadState === "loading" && <EegDockPanelChip>Loading…</EegDockPanelChip>}
      </EegDockPanelStatus>

      <div className="ai-triage-queue__banners" data-testid="ai-triage-banners">
        {error && (
          <div className="ai-triage-queue__error" role="alert">
            {error}
          </div>
        )}
        {blockingCount > 0 && (
          <div
            className="ai-triage-queue__notice"
            data-testid="ai-triage-pending-notice"
            title="Confirm, reject, or mark false positive first to enable Complete triage. Interim draft still available from confirmed events only."
          >
            {blockingCount} item(s) still need disposition before Complete triage.
          </div>
        )}
        {blockingCount === 0 && !explanationGate.ready && (
          <div
            className="ai-triage-queue__notice"
            data-testid="ai-triage-explanation-notice"
            title={explanationGate.warnings.join("; ") || "AI_EXPLANATION_ACK"}
          >
            Explainability incomplete — confirm/reject with saliency acknowledgement
            before Complete.
          </div>
        )}
        {closeError && (
          <div className="ai-triage-queue__error" role="alert">
            {closeError}
          </div>
        )}
        {closeMessage && (
          <div
            className="ai-triage-queue__success"
            data-testid="ai-triage-close-result"
            role="status"
          >
            {closeMessage}{" "}
            {draftReportId ? (
              <a href={`/reports/${draftReportId}`}>Open unsigned draft</a>
            ) : null}
          </div>
        )}
      </div>

      <EegDockPanelList
        className="ai-triage-queue__list"
        role="list"
        aria-label="AI triage candidate queue"
      >
        {filtered.length === 0 && (
          <div className="eeg-dock-panel-chrome__empty ai-triage-queue__empty">
            {summary.total === 0 ? (
              <>
                <div>No AI candidates yet for this recording.</div>
                <div className="text-muted small mt-1">
                  EEG import alone does not create triage candidates. Use{" "}
                  <strong>Run local detectors</strong> on a loaded waveform
                  window, or <strong>Import detector run</strong> with a
                  governed run ID.
                </div>
              </>
            ) : (
              <>
                <div>No candidates match the current filters.</div>
                <div className="text-muted small mt-1">
                  Try Disposition = All, lower Min confidence, or clear Search
                  ({summary.total} candidate
                  {summary.total === 1 ? "" : "s"} hidden).
                </div>
              </>
            )}
          </div>
        )}
        {filtered.map((candidate) => (
          <EegDockPanelCard
            key={candidate.candidateId}
            className={`ai-triage-queue__item is-${candidate.disposition.status}`}
            role="listitem"
            data-testid={`ai-triage-candidate-${candidate.candidateId}`}
          >
            <div className="ai-triage-queue__item-main">
              <div className="eeg-dock-panel-chrome__card-title ai-triage-queue__item-title">
                {categoryFilterActive &&
                (candidate.disposition.status === "pending" ||
                  candidate.disposition.status === "deferred" ||
                  candidate.disposition.status === "escalated") ? (
                  <label className="ai-triage-queue__item-select">
                    <input
                      type="checkbox"
                      data-testid={`ai-triage-select-${candidate.candidateId}`}
                      checked={selectedIds.has(candidate.candidateId)}
                      disabled={bulkBusy || busyId === candidate.candidateId}
                      aria-label={`Select candidate ${candidate.label}`}
                      onChange={(event) =>
                        toggleSelect(candidate.candidateId, event.target.checked)
                      }
                    />
                  </label>
                ) : null}
                <span className="ai-triage-queue__badge">
                  {AI_TRIAGE_CATEGORY_LABELS[candidate.category]}
                </span>
                <strong>{candidate.label}</strong>
                <span className="ai-triage-queue__time">
                  {formatTime(candidate.startTimeSeconds)}
                  {candidate.endTimeSeconds != null
                    ? `–${formatTime(candidate.endTimeSeconds)}`
                    : ""}
                </span>
              </div>
              <div className="eeg-dock-panel-chrome__meta ai-triage-queue__meta">
                <span>
                  Confidence{" "}
                  {candidate.confidence == null
                    ? "n/a"
                    : `${(candidate.confidence * 100).toFixed(0)}%`}
                </span>
                <span>
                  Model {candidate.model.modelName}@{candidate.model.modelVersion}
                </span>
                <span>
                  Channels{" "}
                  {candidate.channels.length
                    ? candidate.channels.join(", ")
                    : "n/a"}
                </span>
                <span>Disposition {candidate.disposition.status}</span>
              </div>
              {(() => {
                const explanation = candidateToExplanationArtifact(candidate);
                return (
                  <details
                    className="ai-triage-queue__explanation"
                    data-testid={`ai-triage-explanation-${candidate.candidateId}`}
                  >
                    <summary>Rationale & evidence</summary>
                    <div className="ai-triage-queue__explanation-text">
                      {explanation.explanationText}
                    </div>
                    <div className="eeg-dock-panel-chrome__meta ai-triage-queue__explanation-meta">
                      <span title={explanation.saliencyUri}>
                        Saliency {explanation.saliencyUri}
                      </span>
                      <span title={explanation.sourceSegmentUri}>
                        Segment {explanation.sourceSegmentUri}
                      </span>
                    </div>
                  </details>
                );
              })()}
              <details className="ai-triage-queue__provenance">
                <summary>Provenance</summary>
                <dl>
                  <div>
                    <dt>Thresholds</dt>
                    <dd>{JSON.stringify(candidate.model.thresholds)}</dd>
                  </div>
                  <div>
                    <dt>Input features</dt>
                    <dd>
                      {candidate.model.inputFeatures.length
                        ? candidate.model.inputFeatures.join(", ")
                        : "n/a"}
                    </dd>
                  </div>
                  <div>
                    <dt>Detector run</dt>
                    <dd>{candidate.model.detectorRunId || "n/a"}</dd>
                  </div>
                  <div>
                    <dt>Audit trail</dt>
                    <dd>
                      {candidate.auditTrail.length
                        ? candidate.auditTrail
                            .map(
                              (entry) =>
                                `${entry.atIso}: ${entry.action}${
                                  entry.falsePositiveClass
                                    ? ` (${entry.falsePositiveClass})`
                                    : ""
                                }`,
                            )
                            .join(" · ")
                        : "No human dispositions yet"}
                    </dd>
                  </div>
                </dl>
              </details>
            </div>
            <EegDockPanelActions className="ai-triage-queue__actions">
              <button
                className="eeg-dock-panel-chrome__btn"
                type="button"
                aria-label={`Jump to raw EEG at ${candidate.startTimeSeconds.toFixed(1)} seconds`}
                onClick={() => {
                  onSeek?.(candidate.startTimeSeconds);
                  const endSeconds =
                    candidate.endTimeSeconds != null &&
                    Number.isFinite(candidate.endTimeSeconds)
                      ? candidate.endTimeSeconds
                      : candidate.startTimeSeconds + 0.2;
                  onFieldEventHint?.({
                    startSeconds: candidate.startTimeSeconds,
                    endSeconds: Math.max(
                      endSeconds,
                      candidate.startTimeSeconds + 0.04,
                    ),
                    label: candidate.label || candidate.category,
                  });
                }}
              >
                Jump
              </button>
              <button
                className="eeg-dock-panel-chrome__btn"
                type="button"
                disabled={busyId === candidate.candidateId}
                aria-label={`Confirm candidate ${candidate.label}`}
                onClick={() => void dispose(candidate, "confirmed")}
              >
                Confirm
              </button>
              <button
                className="eeg-dock-panel-chrome__btn"
                type="button"
                disabled={busyId === candidate.candidateId}
                aria-label={`Reject candidate ${candidate.label}`}
                onClick={() => void dispose(candidate, "rejected")}
              >
                Reject
              </button>
              <button
                className="eeg-dock-panel-chrome__btn"
                type="button"
                disabled={busyId === candidate.candidateId}
                aria-label={`Mark candidate ${candidate.label} as false positive`}
                onClick={() => void dispose(candidate, "false_positive")}
              >
                False +
              </button>
            </EegDockPanelActions>
          </EegDockPanelCard>
        ))}
      </EegDockPanelList>
    </div>
  );
}

export default function AITriageQueuePanel(props: AITriageQueuePanelProps) {
  return (
    <RegulatoryPhaseGate
      phase={3}
      fallback={
        <div className="eeg-dock-panel-chrome__note ai-triage-queue ai-triage-queue--gated" data-testid="ai-triage-queue-gated">
          <p>
            AI triage is Phase 3 gated and not enabled in this regulatory
            configuration. Candidates remain unavailable until clearance.
          </p>
        </div>
      }
    >
      <AITriageQueuePanelInner {...props} />
    </RegulatoryPhaseGate>
  );
}
